import React, { useRef, useState, useEffect } from 'react';
import { Stage, Layer, Rect } from 'react-konva';
import styled from 'styled-components';
import { FloorPlan, FloorPlanItem, Position } from '../types';
import TableItem from './items/TableItem';
import ChairItem from './items/ChairItem';
import StageItem from './items/StageItem';
import WallItem from './items/WallItem';
import DecorItem from './items/DecorItem';
import BarItem from './items/BarItem';
import BuffetItem from './items/BuffetItem';
import CentrePoleItem from './items/CentrePoleItem';
import DanceFloorItem from './items/DanceFloorItem';

// Conversion factor for feet to pixels (1 foot = 10 pixels)
const FEET_TO_PIXELS = 10;

interface FloorPlanCanvasProps {
  floorPlan: FloorPlan;
  onItemMove?: (id: string, position: Position, rotation?: number) => void;
  onItemSelect?: (id: string | null) => void;
  selectedItemId?: string | null;
  scale?: number;
  useFootGrid?: boolean;
  snapToGrid?: boolean;
}

const CanvasContainer = styled.div`
  border: 1px solid #ccc;
  background-color: #f5f5f5;
  overflow: hidden;
  width: 100%;
  height: 100%;
  position: relative;
`;

const DimensionLabel = styled.div`
  position: absolute;
  font-size: 12px;
  color: #666;
  z-index: 10;
  user-select: none;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 2px;
  padding: 2px 6px;
`;

const LengthLabel = styled(DimensionLabel)`
  bottom: 5px;
  left: 50%;
  transform: translateX(-50%);
`;

const WidthLabel = styled(DimensionLabel)`
  top: 50%;
  right: 5px;
  transform: translateY(-50%) rotate(90deg);
`;

const FloorPlanCanvas: React.FC<FloorPlanCanvasProps> = ({
  floorPlan,
  onItemMove,
  onItemSelect,
  selectedItemId,
  scale = 1,
  useFootGrid = false,
  snapToGrid = false
}) => {
  const stageRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [stagePos, setStagePos] = useState<Position>({ x: 0, y: 0 });
  const [stageScale, setStageScale] = useState<number>(scale);
  const [stageSize, setStageSize] = useState({ width: 0, height: 0 });

  // Auto-fit floor plan when dimensions change
  useEffect(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const containerHeight = containerRef.current.offsetHeight;
      
      setStageSize({
        width: containerWidth,
        height: containerHeight
      });

      // Auto-scale and center the floor plan
      const padding = 40; // Padding around the floor plan
      const scaleX = (containerWidth - padding * 2) / floorPlan.size.width;
      const scaleY = (containerHeight - padding * 2) / floorPlan.size.height;
      const newScale = Math.min(scaleX, scaleY, 1); // Don't scale up beyond 1:1
      
      setStageScale(newScale);
      
      // Center the floor plan
      const centerX = containerWidth / 2 - (floorPlan.size.width * newScale) / 2;
      const centerY = containerHeight / 2 - (floorPlan.size.height * newScale) / 2;
      
      setStagePos({ x: centerX, y: centerY });
    }
  }, [floorPlan.size.width, floorPlan.size.height]);

  const handleWheel = (e: any) => {
    e.evt.preventDefault();
    
    // Reduce the zoom factor for less sensitivity (was 1.1)
    const scaleBy = 1.05;
    const stage = stageRef.current;
    const oldScale = stage.scaleX();
    
    // Add a throttle to limit how quickly zoom can change
    // Ignore very small delta changes for smoother zooming
    if (Math.abs(e.evt.deltaY) < 5) return;
    
    const pointerPosition = stage.getPointerPosition();
    const mousePointTo = {
      x: (pointerPosition.x - stage.x()) / oldScale,
      y: (pointerPosition.y - stage.y()) / oldScale,
    };
    
    // Calculate new scale - reduce sensitivity by applying a smaller change
    const direction = e.evt.deltaY < 0 ? 1 : -1;
    const newScale = oldScale * Math.pow(scaleBy, direction);
    
    // Enforce min and max scale limits
    const minScale = 0.1;
    const maxScale = 3;
    const limitedScale = Math.max(minScale, Math.min(maxScale, newScale));
    
    setStageScale(limitedScale);
    setStagePos({
      x: pointerPosition.x - mousePointTo.x * limitedScale,
      y: pointerPosition.y - mousePointTo.y * limitedScale,
    });
  };

  const handleDragStart = (e: any) => {
    const id = e.target.id();
    if (onItemSelect) {
      onItemSelect(id);
    }
  };

  const handleDragEnd = (e: any) => {
    const id = e.target.id();
    const position = { x: e.target.x(), y: e.target.y() };
    
    if (onItemMove) {
      onItemMove(id, position);
    }
  };

  const handleRotate = (id: string, rotation: number) => {
    const item = floorPlan.items.find(item => item.id === id);
    if (item && onItemMove) {
      // Pass the new rotation along with the existing position
      onItemMove(id, item.position, rotation);
    }
  };

  const handleItemClick = (e: any) => {
    // Get the item ID from the clicked shape or its parent group
    // Konva shapes can be nested, so we need to traverse up to find the ID
    let target = e.target;
    while (target && !target.id()) {
      target = target.parent;
    }
    
    const id = target ? target.id() : null;
    
    if (id && onItemSelect) {
      // Prevent event from bubbling up to stage
      e.cancelBubble = true;
      onItemSelect(id);
    }
  };

  // Handler for clicking on the stage background (deselect items)
  const handleStageClick = (e: any) => {
    // Only deselect if we clicked the stage directly (not an item)
    if (e.target === e.currentTarget || e.target.name() === 'background') {
      if (onItemSelect) {
        onItemSelect(null);
      }
    }
  };

  const renderItem = (item: FloorPlanItem) => {
    const isSelected = selectedItemId === item.id;
    const commonProps = {
      id: item.id,
      position: item.position,
      size: item.size,
      rotation: item.rotation,
      isSelected,
      onDragStart: handleDragStart,
      onDragEnd: handleDragEnd,
      onClick: handleItemClick,
      onRotate: handleRotate
    };

    switch (item.type) {
      case 'table':
        return <TableItem key={item.id} {...commonProps} item={item} />;
      case 'chair':
        return <ChairItem key={item.id} {...commonProps} item={item} />;
      case 'stage':
        return <StageItem key={item.id} {...commonProps} item={item} />;
      case 'wall':
        return <WallItem key={item.id} {...commonProps} item={item} />;
      case 'bar':
        return <BarItem key={item.id} {...commonProps} item={item} />;
      case 'buffet':
        return <BuffetItem key={item.id} {...commonProps} item={item} />;
      case 'decor':
        return <DecorItem key={item.id} {...commonProps} item={item} />;
      case 'centrePole':
        return <CentrePoleItem key={item.id} {...commonProps} item={item} />;
      case 'danceFloor':
        return <DanceFloorItem key={item.id} {...commonProps} item={item} />;
      default:
        return null;
    }
  };

  // Draw grid on floor plan
  const drawGrid = () => {
    // 1 foot = 10 pixels, so grid size is 10 pixels
    const gridSize = FEET_TO_PIXELS; // 1 foot grid
    const numLinesX = Math.ceil(floorPlan.size.width / gridSize);
    const numLinesY = Math.ceil(floorPlan.size.height / gridSize);
    const gridLines = [];

    // Draw horizontal grid lines
    for (let i = 0; i <= numLinesY; i++) {
      const y = i * gridSize;
      const lineColor = i % 5 === 0 ? '#cccccc' : '#e0e0e0'; // Darker line every 5 feet
      const lineThickness = i % 5 === 0 ? 1 : 0.5;
      
      gridLines.push(
        <Rect
          key={`h-${i}`}
          x={0}
          y={y}
          width={floorPlan.size.width}
          height={lineThickness}
          fill={lineColor}
        />
      );
    }

    // Draw vertical grid lines
    for (let i = 0; i <= numLinesX; i++) {
      const x = i * gridSize;
      const lineColor = i % 5 === 0 ? '#cccccc' : '#e0e0e0'; // Darker line every 5 feet
      const lineThickness = i % 5 === 0 ? 1 : 0.5;
      
      gridLines.push(
        <Rect
          key={`v-${i}`}
          x={x}
          y={0}
          width={lineThickness}
          height={floorPlan.size.height}
          fill={lineColor}
        />
      );
    }

    return gridLines;
  };

  // Convert dimensions from pixels to feet for display
  const displayDimensions = {
    length: Math.round((floorPlan.size.width / FEET_TO_PIXELS) * 10) / 10,
    width: Math.round((floorPlan.size.height / FEET_TO_PIXELS) * 10) / 10
  };

  return (
    <CanvasContainer ref={containerRef}>
      <LengthLabel>Length: {displayDimensions.length} ft</LengthLabel>
      <WidthLabel>Width: {displayDimensions.width} ft</WidthLabel>
      
      <Stage
        ref={stageRef}
        width={stageSize.width}
        height={stageSize.height}
        onWheel={handleWheel}
        scaleX={stageScale}
        scaleY={stageScale}
        x={stagePos.x}
        y={stagePos.y}
        draggable
        onClick={handleStageClick}
        onDblClick={() => onItemSelect && onItemSelect(null)}
      >
        <Layer>
          {/* Background */}
          <Rect
            x={-500}
            y={-500}
            width={floorPlan.size.width + 1000}
            height={floorPlan.size.height + 1000}
            fill="#f0f0f0"
            name="background"
          />
          
          {/* Floor plan background */}
          <Rect
            x={0}
            y={0}
            width={floorPlan.size.width}
            height={floorPlan.size.height}
            fill="#fff"
            stroke="#bbb"
            strokeWidth={2}
            name="background"
          />

          {/* Grid */}
          {drawGrid()}
          
          {/* Render all floor plan items */}
          {floorPlan.items.map(renderItem)}
        </Layer>
      </Stage>
    </CanvasContainer>
  );
};

export default FloorPlanCanvas; 